import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';

import {
  GachaShopConfigDataFragment,
  SetGachaShopEnabledMutation,
  SetGachaShopEnabledMutationVariables,
} from '../../../../../../__gqltypes__';
import { SET_GACHA_SHOP_ENABLED } from '../graphql';

type Props = {
  config: GachaShopConfigDataFragment | null;
  onChanged: (config: GachaShopConfigDataFragment) => void;
};

/**
 * The feature kill switch (GachaShopConfig.enabled, default OFF). Checked before every campaign:
 * while it is off players get no shop, no entry points, no openings and no campaign-start pushes.
 */
export function GachaKillSwitch({ config, onChanged }: Props): JSX.Element {
  const [pending, setPending] = useState<boolean | null>(null);
  const [setEnabled, { loading: saving, error }] = useMutation<
    SetGachaShopEnabledMutation,
    SetGachaShopEnabledMutationVariables
  >(SET_GACHA_SHOP_ENABLED, {
    onCompleted: (data) => onChanged(data.setGachaShopEnabled),
  });

  const enabled = config?.enabled;

  const apply = () => {
    // Single-flight: the confirm button is disabled while the mutation runs, and a stray second call is ignored.
    if (pending === null || saving) {
      return;
    }
    setEnabled({ variables: { enabled: pending } })
      .catch(() => {
        // surfaced through error
      })
      .finally(() => setPending(null));
  };

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center">
        <h4 className="mb-0 mr-3">Gacha Shop</h4>
        {enabled === undefined ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <>
            <Form.Check
              type="switch"
              id="gacha-shop-feature-enabled"
              label=""
              checked={enabled}
              disabled={saving}
              onChange={(e) => setPending(e.target.checked)}
            />
            <Badge variant={enabled ? 'success' : 'danger'}>{enabled ? 'Feature ON' : 'Feature OFF'}</Badge>
            {saving && <Spinner animation="border" size="sm" className="ml-2" />}
          </>
        )}
      </div>
      <small className="text-muted">
        Feature kill switch, checked before every campaign. It is separate from each campaign&apos;s own Enabled toggle.
      </small>
      {enabled === false && (
        <Alert variant="danger" className="mt-2 mb-0">
          <i className="fas fa-power-off mr-2" />
          <strong>Gacha Shop is OFF for players.</strong> No shop, no Shop/Home banners, no box openings and no
          campaign-start pushes, whatever the campaigns below say. Campaigns can still be prepared here.
        </Alert>
      )}
      {error && (
        <Alert variant="danger" className="mt-2">
          {error.message}
        </Alert>
      )}

      <Modal show={pending !== null} onHide={() => !saving && setPending(null)}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">{pending ? 'Turn the Gacha Shop on?' : 'Turn the Gacha Shop off?'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pending
            ? 'Every enabled campaign inside its window becomes visible to its audience, and the start push of a ' +
              'PROD or BOTH campaign can go out. It can take a few minutes to show in the app.'
            : 'The shop and every entry point disappear for all players, openings are refused and no campaign-start ' +
              'push is sent. Player progress (claims, pity) is kept.'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPending(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant={pending ? 'success' : 'danger'} onClick={apply} disabled={saving}>
            {saving && <Spinner as="span" animation="border" size="sm" className="mr-2" />}
            {pending ? 'Turn on' : 'Turn off'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
